import {inject, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {
  EvaluationCriteriaControllerService,
  EvaluationCriteriaResponse,
  TenderPhaseType,
} from '@frontend/shared-api';
import {
  ProviderScoreCalculationResult,
  ProviderScoreCalculationService,
} from './provider-score-calculation.service';
import {SubCriteriaItem} from '../model/sub-criteria.interfaces';

export interface ProviderTotalScoreResult {
  total: number;
  maxPossibleScore: number;
  percentage: number;
}

export interface ProviderTotalsWithWeightResult {
  totals: Map<string, ProviderTotalScoreResult>;
  totalWeight: number;
}

interface IncludedCriteria {
  ecNumber: number;
  subCriteria: SubCriteriaItem[];
}

@Injectable({
  providedIn: 'root',
})
export class ProviderTotalScoreCalculationService {
  private readonly providerScoreCalculationService = inject(ProviderScoreCalculationService);
  private readonly evaluationCriteriaService = inject(EvaluationCriteriaControllerService);

  async calculateProviderTotalsWithWeight(
    projectId: number,
    phase: TenderPhaseType,
  ): Promise<ProviderTotalsWithWeightResult> {
    const [calculationResult, evaluationCriteria] = await Promise.all([
      this.providerScoreCalculationService.calculateProviderScores(projectId, phase),
      firstValueFrom(this.evaluationCriteriaService.getEvaluationCriteriaByProject({projectId})),
    ]);

    return {
      totals: this.calculateProviderTotalsFromResult(calculationResult, evaluationCriteria),
      totalWeight: this.calculatePhaseWeightFromResult(calculationResult, evaluationCriteria),
    };
  }

  calculateProviderTotalsFromResult(
    calculationResult: ProviderScoreCalculationResult,
    evaluationCriteria: EvaluationCriteriaResponse[],
  ): Map<string, ProviderTotalScoreResult> {
    const includedCriteria = this.getIncludedCriteria(calculationResult);
    const evaluationCriteriaMap = this.getEvaluationCriteriaMap(evaluationCriteria);
    const toolIds = this.getToolIds(calculationResult);

    const totals = new Map<string, ProviderTotalScoreResult>();

    toolIds.forEach((toolId) => {
      const {total, maxPossibleScore} = this.calculateTotalsForProvider(
        toolId,
        includedCriteria,
        evaluationCriteriaMap,
        calculationResult,
      );

      totals.set(toolId, {
        total,
        maxPossibleScore,
        percentage: this.calculatePercentage(total, maxPossibleScore),
      });
    });

    return totals;
  }

  calculatePhaseWeightFromResult(
    calculationResult: ProviderScoreCalculationResult,
    evaluationCriteria: EvaluationCriteriaResponse[],
  ): number {
    const includedCriteria = this.getIncludedCriteria(calculationResult);
    const evaluationCriteriaMap = this.getEvaluationCriteriaMap(evaluationCriteria);

    return includedCriteria.reduce((sum, criteria) => {
      const weight = evaluationCriteriaMap.get(criteria.ecNumber)?.weight ?? 1.0;
      return sum + weight;
    }, 0);
  }

  private getIncludedCriteria(
    calculationResult: ProviderScoreCalculationResult,
  ): IncludedCriteria[] {
    const criteria: IncludedCriteria[] = [];

    calculationResult.subCriteriaByEc.forEach((subCriteria, ecNumber) => {
      const includedSubCriteria = subCriteria.filter((sc) => sc.included === true);
      if (includedSubCriteria.length === 0) return;

      criteria.push({ecNumber, subCriteria: includedSubCriteria});
    });

    return criteria;
  }

  private getEvaluationCriteriaMap(
    evaluationCriteria: EvaluationCriteriaResponse[],
  ): Map<number, EvaluationCriteriaResponse> {
    return new Map(evaluationCriteria.map((criteria) => [criteria.ecNumber, criteria]));
  }

  private getToolIds(calculationResult: ProviderScoreCalculationResult): string[] {
    return Array.from(new Set(calculationResult.allScores.map((score) => score.hrToolDirectusId)));
  }

  private calculateTotalsForProvider(
    toolId: string,
    includedCriteria: IncludedCriteria[],
    evaluationCriteriaMap: Map<number, EvaluationCriteriaResponse>,
    calculationResult: ProviderScoreCalculationResult,
  ): {total: number; maxPossibleScore: number} {
    let totalWeightedSum = 0;
    let totalWeightedMax = 0;

    includedCriteria.forEach((criteria) => {
      const evaluationCriteria = evaluationCriteriaMap.get(criteria.ecNumber);
      const criteriaWeight = evaluationCriteria?.weight ?? 1.0;

      let criteriaSum = 0;
      let criteriaMax = 0;

      criteria.subCriteria.forEach((subCriteria) => {
        const fieldCombination = this.providerScoreCalculationService.getFieldCombination(
          calculationResult.fieldCombinations,
          subCriteria.scNumber,
          toolId,
        );

        if (!fieldCombination) return;

        const subCriteriaWeight = this.getSubCriteriaWeight(
          evaluationCriteria,
          subCriteria.scNumber,
        );

        criteriaSum += fieldCombination.normalizedScore * subCriteriaWeight;
        criteriaMax += fieldCombination.maxPossibleScore * subCriteriaWeight;
      });

      totalWeightedSum += criteriaSum * criteriaWeight;
      totalWeightedMax += this.roundToTwoDecimals(criteriaMax) * criteriaWeight;
    });

    return {total: totalWeightedSum, maxPossibleScore: totalWeightedMax};
  }

  private getSubCriteriaWeight(
    evaluationCriteria: EvaluationCriteriaResponse | undefined,
    subCriteriaNumber: number,
  ): number {
    return (
      evaluationCriteria?.subCriteria?.find(
        (subCriteria) => subCriteria.scNumber === subCriteriaNumber,
      )?.weight ?? 1.0
    );
  }

  private calculatePercentage(total: number, maxPossibleScore: number): number {
    return maxPossibleScore > 0 ? (total / maxPossibleScore) * 100 : 0;
  }

  private roundToTwoDecimals(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
